import { Editor } from '@tiptap/core';
import { buildInternalCanonicalStack } from '../internal/ordered-stack';
import { serializeSectionPresentationMarkdown } from '../sections/section-markdown';
import { stripEmptySectionHeadingPresentationHtml } from '../sections/section-markers';
import { type RendererVersion } from './canonical-renderer.types';

/**
 * Current canonical renderer version. See {@link RendererVersion}.
 */
export const CANONICAL_RENDERER_VERSION: RendererVersion = 'v1';

export type CanonicalMarkdownRenderProfile = {
  editorConstructionMs: number;
  markdownParsingMs: number;
  htmlSerializationMs: number;
  presentationStripMs: number;
  editorDestructionMs: number;
  totalMs: number;
};

export type ProfiledCanonicalHtml = {
  html: string;
  profile: CanonicalMarkdownRenderProfile;
};

/**
 * Headless editor built from the package-private canonical stack
 * (`buildInternalCanonicalStack`), which reproduces the app kit's frozen
 * canonical base, so rendered output always matches what the markdown-native
 * editor shows — never a standalone `marked` pass, whose
 * copy lexes differently from the editor's. Mirrors the round-trip harness.
 */
const assertSupportedVersion = (version: RendererVersion): void => {
  if (version !== CANONICAL_RENDERER_VERSION) {
    throw new Error(`Unsupported canonical renderer version: ${String(version)}`);
  }
};

/**
 * Typed, content-free failure for a conversion attempted outside a DOM
 * environment (task 4.11): ProseMirror's HTML parse/serialize needs a
 * document, and the raw error it would otherwise hit must never carry input
 * content. Checked at call time — importing this module stays SSR-safe, and
 * the version/empty short-circuits above it keep working in Node.
 */
export class CanonicalRendererEnvironmentError extends Error {
  constructor() {
    super('Canonical renderer requires a DOM environment');
    this.name = 'CanonicalRendererEnvironmentError';
  }
}

const assertDomEnvironment = (): void => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    throw new CanonicalRendererEnvironmentError();
  }
};

const now = (): number =>
  typeof performance === 'undefined' ? Date.now() : performance.now();

/**
 * Run `fn` against a fresh headless editor, always destroying it afterwards.
 * Constructed on `@tiptap/core`'s Editor with `element: null` and
 * `injectCSS: false` (task 4.11): a conversion never mounts and must not
 * inject the editor stylesheet as a side effect.
 */
const withHeadlessEditor = <T>(
  fn: (editor: Editor) => T,
  profile: CanonicalMarkdownRenderProfile | null = null
): T => {
  const constructionStartedAt = profile ? now() : 0;
  const editor = new Editor({
    element: null,
    injectCSS: false,
    extensions: [...buildInternalCanonicalStack({ placeholder: '' })],
  });
  if (profile) profile.editorConstructionMs = now() - constructionStartedAt;

  try {
    return fn(editor);
  } finally {
    const destructionStartedAt = profile ? now() : 0;
    editor.destroy();
    if (profile) profile.editorDestructionMs = now() - destructionStartedAt;
  }
};

const renderMarkdownToHtmlInternal = (
  markdown: string,
  version: RendererVersion,
  profile: CanonicalMarkdownRenderProfile | null
): string => {
  assertSupportedVersion(version);
  if (!markdown) return '';
  assertDomEnvironment();

  const totalStartedAt = profile ? now() : 0;
  const html = withHeadlessEditor((editor) => {
    const parsingStartedAt = profile ? now() : 0;
    editor.commands.setContent(markdown, { contentType: 'markdown', emitUpdate: false });
    if (profile) profile.markdownParsingMs = now() - parsingStartedAt;

    const serializationStartedAt = profile ? now() : 0;
    const serializedHtml = editor.getHTML();
    if (profile) profile.htmlSerializationMs = now() - serializationStartedAt;

    const presentationStripStartedAt = profile ? now() : 0;
    const serialized = stripEmptySectionHeadingPresentationHtml(serializedHtml);
    if (profile) profile.presentationStripMs = now() - presentationStripStartedAt;
    return serialized;
  }, profile);
  if (profile) profile.totalMs = now() - totalStartedAt;
  return html;
};

/**
 * Render stored canonical GFM markdown to HTML for copy / export / print.
 *
 * The HTML is produced by the editor's own `setContent` + `getHTML` pipeline,
 * so it is byte-for-byte the markup the markdown-native editor renders
 * (`<section data-section-title>`, `<h1>`–`<h5>`, `<ul>`/`<ol>`, `<table>`,
 * inline marks). Deterministic for a given input: ProseMirror serializes the
 * document tree depth-first with no state carried between calls.
 */
export function renderMarkdownToHtml(
  markdown: string,
  version: RendererVersion = CANONICAL_RENDERER_VERSION
): string {
  return renderMarkdownToHtmlInternal(markdown, version, null);
}

/**
 * Benchmark-only sibling of {@link renderMarkdownToHtml}. It follows the same
 * fresh-editor path and returns content-free phase timings beside the HTML.
 * Callers must never persist the returned HTML in performance artifacts.
 */
export function profileMarkdownToHtml(
  markdown: string,
  version: RendererVersion = CANONICAL_RENDERER_VERSION
): ProfiledCanonicalHtml {
  const profile: CanonicalMarkdownRenderProfile = {
    editorConstructionMs: 0,
    markdownParsingMs: 0,
    htmlSerializationMs: 0,
    presentationStripMs: 0,
    editorDestructionMs: 0,
    totalMs: 0,
  };
  return {
    html: renderMarkdownToHtmlInternal(markdown, version, profile),
    profile,
  };
}

/**
 * Inverse of {@link renderMarkdownToHtml}: serialize HTML back to canonical
 * GFM markdown through the same kit. Used for the copy `text/plain` sibling
 * after copy-settings transforms, keeping HTML→markdown inside the
 * markdown-native pipeline rather than the legacy regex serializers.
 */
export function renderHtmlToMarkdown(
  html: string,
  version: RendererVersion = CANONICAL_RENDERER_VERSION
): string {
  assertSupportedVersion(version);
  if (!html) return '';
  assertDomEnvironment();

  return withHeadlessEditor((editor) => {
    editor.commands.setContent(stripEmptySectionHeadingPresentationHtml(html), {
      contentType: 'html',
      emitUpdate: false,
    });
    const markdownManager = editor.markdown;
    return markdownManager
      ? serializeSectionPresentationMarkdown(editor.getJSON().content ?? [], (node) =>
          markdownManager.serialize(node)
        )
      : '';
  });
}
